/**
 * Feature 298 — the kitchen display's languages.
 *
 * Italian is the source and the fallback for a missing text. The language a
 * screen opens in is the one stored on it (chosen in its settings), else the
 * installation's (`brand.defaultLanguage`). The browser is deliberately not
 * read: see `initialKdsLanguage`.
 */
export const SUPPORTED_LANGUAGES = ["it", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const FALLBACK_LANGUAGE: SupportedLanguage = "it";
export const NAMESPACE = "kds";
export const STORAGE_KEY = "kds.ui_language";

export function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return typeof value === "string" && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * The stored choice, else the installation's language. Kitchen tablets often
 * run an English system in an Italian kitchen; following the browser would
 * switch a Voxli screen to English by itself in front of the cooks.
 */
export function initialKdsLanguage(
  stored: string | null | undefined,
  installation: string,
): SupportedLanguage {
  if (isSupportedLanguage(stored)) return stored;
  return isSupportedLanguage(installation) ? installation : FALLBACK_LANGUAGE;
}

/**
 * The translator a pure module takes from its caller. `src/lib/**` must not
 * import the i18n module (it is unit-tested without React), so a function that
 * builds a text receives the component's `t`.
 */
export type KdsT = (key: string, fallback: string, values?: Record<string, unknown>) => string;
